import getHistory from 'globalHistory';
import moment from 'moment';
import _ from 'lodash';

import { format } from '@breezeway/brzw-react-core';
import {
  CALENDAR_CELL_TYPES,
  CALENDAR_COLUMNS_NUMBER,
  CALENDAR_DATA_DATE_KEY_FORMAT,
  SCHEDULE_DATE_OPERATION,
  SCHEDULE_MODE,
  SCHEDULE_ROUTE_DATE_FORMAT,
  SCHEDULE_ROUTE_BASE,
  SCHEDULE_SUBJECT,
} from './constants';
import * as taskHelper from '../utils/task';
import * as peopleHelpers from '../utils/people_helpers';

const { formatDate } = format.datetime;

/**
 * @param {moment|Date\String} activeDate
 * @return {moment}
 */
export const cloneDate = activeDate => {
  const isMoment = moment.isMoment(activeDate);

  return isMoment ? activeDate.clone() : moment(activeDate);
};

/**
 * @param {moment|Date\String} activeDate
 * @return {Boolean}
 */
export const isTodayDate = activeDate => moment().isSame(activeDate, 'day');

/**
 * @param {moment|Date\String} activeDate
 * @return {Boolean}
 */
export const isDayBeforeTodayDate = activeDate => moment(activeDate).isBefore(moment(), 'day');
/**
 * @param {moment|Date\String} activeDate
 * @return {Boolean}
 */
export const isDayAfterTodayDate = activeDate => moment(activeDate).isAfter(moment(), 'day');
/**
 * @param {Moment} momentDate
 * @return {Moment}
 */
export const getMonthBegin = momentDate => cloneDate(momentDate).startOf('month');

/**
 * @param {Moment} momentDate
 * @return {Moment}
 */
export const getMonthEnd = momentDate => cloneDate(momentDate).endOf('month');

/**
 * @param {Moment} momentDate
 * @return {Moment}
 */
export const getWeekBegin = momentDate => cloneDate(momentDate).startOf('week');

/**
 * @param {Moment} momentDate
 * @return {Moment}
 */
export const getWeekEnd = momentDate => cloneDate(momentDate).endOf('week');

/**
 * @param {Moment} momentDate
 * @return {Moment}
 */
export const getTaskDateMoment = task =>
  moment(_.get(task, 'start_date'), CALENDAR_DATA_DATE_KEY_FORMAT);

/**
 * @param {Moment} momentDate
 * @return {Number}
 */
export const getMonthBeginDay = momentDate => getMonthBegin(momentDate).day();

/**
 * @param {Moment} momentDate
 * @return {String}
 */
export const formatGroupDateKey = momentDate =>
  formatDate(momentDate, null, CALENDAR_DATA_DATE_KEY_FORMAT);

/**
 * @param {Moment} momentDate
 * @return {String}
 */
export const getMonthName = momentDate => formatDate(momentDate, null, 'MMMM');

/**
 * @param {Moment} date
 * @param {Number} rowIndex
 * @param {Number} columnIndex
 * @return {Number}
 */
export const calculateCellPosition = (date, rowIndex, columnIndex) => {
  const activeDate = cloneDate(date);
  const monthBeginDay = getMonthBeginDay(activeDate);

  return rowIndex * CALENDAR_COLUMNS_NUMBER + (columnIndex + 1 - monthBeginDay);
};

/**
 * @param {Moment} date
 * @return {Number}
 */
export const calculateRowsNumber = date => {
  const momentDate = cloneDate(date);
  const daysInMonth = momentDate.daysInMonth();
  const startDay = getMonthBeginDay(momentDate);
  const daysSummary = daysInMonth + startDay;

  return Math.ceil(daysSummary / CALENDAR_COLUMNS_NUMBER);
};

/**
 * @param {Number} iterationsNumber
 * @param {Function} mapFunction
 * @return {Array.<Object>}
 */
export const mapElements = (iterationsNumber = 0, mapFunction = () => {}) => {
  const elements = [];

  if (_.isFunction(mapFunction)) {
    _.times(iterationsNumber, index => {
      const element = mapFunction(index);

      if (element) {
        elements.push(element);
      }
    });
  }

  return elements;
};

/**
 * @param {Moment} date
 * @param {Number} cellPosition
 * @return {Object}
 */
export const getCorrectDateByCellPosition = (date, cellPosition) => {
  const activeDate = cloneDate(date);
  let year = activeDate.year();
  let month = activeDate.month();
  let day = cellPosition;
  const daysInMonth = activeDate.daysInMonth();

  if (cellPosition <= 0) {
    const newDate = cloneDate(activeDate).subtract(1, 'month');
    const cellPositionWithPositiveSign = cellPosition === 0 ? cellPosition : cellPosition * -1;
    day = newDate.daysInMonth() - cellPositionWithPositiveSign;
    month = newDate.month();
    year = newDate.year();
  } else if (cellPosition > daysInMonth) {
    const newDate = cloneDate(activeDate).add(1, 'month');
    day = cellPosition - daysInMonth;
    month = newDate.month();
    year = newDate.year();
  }

  return {
    day,
    month,
    year,
  };
};

/**
 * @param {Moment} date
 * @param {Number} rowIndex
 * @param {Number} columnIndex
 * @param {Boolean} thirdTheme
 * @return {String}
 */
export const getCellType = (date, rowIndex, columnIndex, thirdTheme) => {
  const activeDate = cloneDate(date);
  const monthBeginDay = getMonthBeginDay(activeDate);
  const daysInMonth = activeDate.daysInMonth();
  const cellPosition = calculateCellPosition(activeDate, rowIndex, columnIndex);

  const isFirstRow = rowIndex === 0;
  const isColumnFromPast = columnIndex < monthBeginDay;
  const isCellFromFuture = cellPosition > daysInMonth;
  const isFirstCellsFromPast = isFirstRow && isColumnFromPast;

  if (thirdTheme && isFirstCellsFromPast) {
    return CALENDAR_CELL_TYPES.DAY_BEFORE;
  } else if (thirdTheme && isCellFromFuture) {
    return CALENDAR_CELL_TYPES.DAY_AFTER;
  }

  if (!thirdTheme && (isFirstCellsFromPast || isCellFromFuture)) {
    return CALENDAR_CELL_TYPES.EMPTY;
  }

  return CALENDAR_CELL_TYPES.DAY;
};

/**
 * @param {Moment} date
 * @param {Number} cellPosition
 * @return {Moment}
 */
export const getCellDate = (date, cellPosition) => {
  const activeDate = cloneDate(date);
  const { day, month, year } = getCorrectDateByCellPosition(activeDate, cellPosition);

  return moment({
    day,
    month,
    year,
  });
};

/**
 * @param {Moment} cellDate
 * @return {String}
 */
export const getDataDateKey = cellDate => formatDate(cellDate, null, CALENDAR_DATA_DATE_KEY_FORMAT);

/**
 * @param {Moment} cellDate
 * @param {Array.<Object>} reservations
 * @return {Object}
 */
export const getReservationProps = (cellDate, reservations) => {
  const reservationProps = {
    alerts: [],
    checkin: null,
    checkout: null,
    occupied: null,
  };
  let numberOfOccupied = 0;

  _.forEach(reservations, reservation => {
    reservationProps.numberOfOccupied = 0;

    if (!_.isEmpty(_.get(reservation, 'alert'))) {
      reservationProps.alerts.push({ ..._.get(reservation, 'alert'), id: reservation.id });
    }

    if (cellDate.isSame(reservation.checkin_date, 'day')) {
      reservationProps.checkin = reservation;
    }

    if (cellDate.isSame(reservation.checkout_date, 'day')) {
      reservationProps.checkout = reservation;
    }

    if (
      cellDate.isAfter(reservation.checkin_date, 'day') &&
      cellDate.isBefore(reservation.checkout_date, 'day')
    ) {
      numberOfOccupied += 1;
      reservationProps.occupied = reservation;
    }
  });

  // Set reservation as empty when reservationProps are empty
  if (_.every(reservationProps, _.isEmpty)) {
    return {
      ...reservationProps,
      empty: true,
    };
  }

  return { ...reservationProps, numberOfOccupied };
};

/**
 * @param {Object}
 * @return {Array.<Number>}
 */
export const getTasksIdsFromRelated = subjectsRelated => {
  const tasksIds = [];

  _.forEach(subjectsRelated, subjectRelated => {
    const { tasks } = subjectRelated;

    _.forEach(tasks, task => {
      tasksIds.push(task.id);
    });
  });

  return tasksIds;
};

/**
 * @param {Object} task
 * @return {Object}
 */
export const getSingleTaskProps = task => {
  const departmentCode = taskHelper.getDepartmentCode(task);
  const lifeStatus = taskHelper.getLifeStatus(task);
  const isInProgress = taskHelper.isTaskInProgress(task);
  const isDraft = taskHelper.isDraft(task);
  const taskId = _.get(task, 'id');
  const taskSeries = taskHelper.getTaskSeriesId(task);
  const isPaused = taskHelper.isTaskPaused(task);
  const isStarted = taskHelper.isTaskStarted(task);
  const taskStatus = _.get(task, 'type_task_status', {});
  const taskCommentCount = _.get(task, 'comment_count', 0);
  const taskIssueCount = _.get(task, 'issue_count', 0);
  const priorityCode = _.get(task, 'type_priority.code');

  const onTaskClick = () => {
    getHistory().push(`/task/${taskId}`);
  };

  return {
    clickable: true,
    departmentCode,
    isDraft,
    isPaused,
    isStarted,
    lifeStatus,
    onTaskClick,
    priorityCode,
    showTiming: isInProgress,
    taskCommentCount,
    taskId,
    taskIssueCount,
    taskName: task.name,
    taskSeries,
    taskStatus,
  };
};

/**
 * @param {Moment} activeDate
 * @param {Array.<Object>} tasks
 * @return {Array.<Object>}
 */
export const filterTasksByDate = (activeDate, tasks) => {
  const filteredTasks = _.filter(tasks, task => {
    const taskMoment = getTaskDateMoment(task);

    return (
      taskMoment.isValid() &&
      activeDate.isSame(taskMoment, 'year') &&
      activeDate.isSame(taskMoment, 'month')
    );
  });

  return _.sortBy(filteredTasks, 'start_date');
};

/**
 * @param {Moment} activeDate
 * @param {Array.<Object>} tasks
 * @return {Array.<Object>}
 */
export const filterTasksByDateWithLastAndNextMonth = (activeDate, tasks) => {
  const filteredTasks = _.filter(tasks, task => {
    const taskMoment = getTaskDateMoment(task);
    const oldMonth = cloneDate(activeDate).subtract(1, 'month');
    const nextMonth = cloneDate(activeDate).add(1, 'month');
    const taskIsFromCurrentMonth =
      activeDate.isSame(taskMoment, 'year') && activeDate.isSame(taskMoment, 'month');
    const taskIsFromPreviousMonth =
      oldMonth.isSame(taskMoment, 'year') && oldMonth.isSame(taskMoment, 'month');
    const taskIsFromNextMonth =
      nextMonth.isSame(taskMoment, 'year') && nextMonth.isSame(taskMoment, 'month');

    return (
      taskMoment.isValid() &&
      (taskIsFromCurrentMonth || taskIsFromPreviousMonth || taskIsFromNextMonth)
    );
  });

  return _.sortBy(filteredTasks, 'start_date');
};

/**
 * @param {moment|Date\String} activeDate
 * @param {String} mode
 * @param {String} subject
 */
export const updateScheduleRoute = (activeDate, mode, subject) => {
  const clonedDate = cloneDate(activeDate);
  const formattedDate = formatDate(clonedDate, false, SCHEDULE_ROUTE_DATE_FORMAT);

  const path = ['', SCHEDULE_ROUTE_BASE, subject, formattedDate, mode].join('/');

  getHistory().push(path);
};

/**
 * @param {moment|Date\String} activeDate
 * @param {Number} modifier
 * @param {String} operation
 * @param {String} unit
 * @return {moment}
 */
export const modifyActiveDate = (
  activeDate,
  modifier = 0,
  operation = SCHEDULE_DATE_OPERATION.ADD,
  unit = 'days',
) => {
  const clonedDate = cloneDate(activeDate);

  return clonedDate[operation](modifier, unit);
};

/**
 * @param {moment|Date\String} activeDate
 * @return {moment}
 */
export const getPreviousWeek = activeDate =>
  modifyActiveDate(activeDate, 1, SCHEDULE_DATE_OPERATION.SUBTRACT, 'weeks');

/**
 * @param {moment|Date\String} activeDate
 * @return {moment}
 */
export const getNextWeek = activeDate =>
  modifyActiveDate(activeDate, 1, SCHEDULE_DATE_OPERATION.ADD, 'weeks');

/**
 * @param {Moment} momentDate
 * @return {Array.<Moment>}
 */
export const getWeekArray = momentDate => {
  const weekBegin = getWeekBegin(momentDate);
  const weekEnd = getWeekEnd(momentDate);
  const weekDays = [];

  let currentDay = cloneDate(weekBegin);

  while (currentDay.isSameOrBefore(weekEnd)) {
    weekDays.push(currentDay);

    currentDay = modifyActiveDate(currentDay, 1);
  }

  return weekDays;
};

const filteredTasksWithSameDay = (iterationDate, tasks) =>
  _.filter(tasks, task => {
    const taskMoment = getTaskDateMoment(task);

    return iterationDate.isSame(taskMoment, 'day');
  });

const filteredReservationsForDay = (iterationDate, reservations) =>
  _.filter(reservations, reservation => {
    const checkinDate = moment(_.get(reservation, 'checkin_date'), CALENDAR_DATA_DATE_KEY_FORMAT);
    const checkoutDate = moment(_.get(reservation, 'checkout_date'), CALENDAR_DATA_DATE_KEY_FORMAT);

    return (
      (iterationDate.isAfter(checkinDate, 'day') || iterationDate.isSame(checkinDate, 'day')) &&
      (iterationDate.isBefore(checkoutDate, 'day') || iterationDate.isSame(checkoutDate, 'day'))
    );
  });

/**
 * @param {Moment} activeDate
 * @param {Array.<Object>} tasks
 * @param {Array.<Object>} reservations
 * @return {Object}
 */
export const groupByDate = (activeDate, tasks, reservations) => {
  const monthBegin = getMonthBegin(activeDate);
  const daysInMonth = activeDate.daysInMonth();
  const dateGroup = {};
  let dayIterator = 0;

  for (dayIterator; dayIterator < daysInMonth; dayIterator += 1) {
    const iterationDate = modifyActiveDate(monthBegin, dayIterator);
    const iterationDateKey = formatGroupDateKey(iterationDate);
    const filteredTasks = filteredTasksWithSameDay(iterationDate, tasks);

    dateGroup[iterationDateKey] = {
      tasks: filteredTasks,
      reservations,
    };
  }

  return dateGroup;
};

/**
 * @param {Moment} activeDate
 * @param {Array.<Object>} tasks
 * @param {Array.<Object>} reservations
 * @return {Object}
 */
export const groupByDateWithMissingDays = (activeDate, tasks, reservations) => {
  const monthBegin = getMonthBegin(activeDate);
  const daysInMonth = activeDate.daysInMonth();
  const daysInWeek = _.size(moment.weekdaysShort());

  const daysBefore = _.invoke(_.invoke(activeDate, 'startOf', 'month'), 'weekday');
  const daysAfter = daysInWeek - 1 - _.invoke(_.invoke(activeDate, 'endOf', 'month'), 'weekday');

  const dateGroup = {};
  let dayIterator = 0;

  const addDataToDateGroup = iterationDate => {
    const iterationDateKey = formatGroupDateKey(iterationDate);
    const filteredTasks = filteredTasksWithSameDay(iterationDate, tasks);
    const filteredReservations = filteredReservationsForDay(iterationDate, reservations);

    dateGroup[iterationDateKey] = {
      tasks: filteredTasks,
      reservations: filteredReservations,
    };
  };

  // how many days are before first day of month
  if (daysBefore > 0) {
    const oldMonth = cloneDate(activeDate)
      .subtract(1, 'month')
      .endOf('month');
    const oldMonthBegin = oldMonth.subtract(daysBefore - 1, 'days');

    for (dayIterator; dayIterator < daysBefore; dayIterator += 1) {
      const iterationDate = modifyActiveDate(oldMonthBegin, dayIterator);
      addDataToDateGroup(iterationDate);
    }
  }

  dayIterator = 0;

  for (dayIterator; dayIterator < daysInMonth; dayIterator += 1) {
    const iterationDate = modifyActiveDate(monthBegin, dayIterator);
    addDataToDateGroup(iterationDate);
  }

  dayIterator = 0;

  // how many days are after last day of month
  if (daysAfter > 0) {
    const nextMonth = cloneDate(activeDate)
      .add(1, 'month')
      .startOf('month');

    for (dayIterator; dayIterator < daysAfter; dayIterator += 1) {
      const iterationDate = modifyActiveDate(nextMonth, dayIterator);
      addDataToDateGroup(iterationDate);
    }
  }

  return dateGroup;
};

/**
 * @param {String} subjectType
 * @param {Object} callbacks
 */
export const subjectTypeGate = (subjectType, callbacks = {}) => {
  switch (subjectType) {
    case SCHEDULE_SUBJECT.PROPERTY:
    default: {
      if (_.isFunction(callbacks[SCHEDULE_SUBJECT.PROPERTY])) {
        callbacks[SCHEDULE_SUBJECT.PROPERTY]();
      }

      break;
    }
    case SCHEDULE_SUBJECT.USER: {
      if (_.isFunction(callbacks[SCHEDULE_SUBJECT.USER])) {
        callbacks[SCHEDULE_SUBJECT.USER]();
      }

      break;
    }
  }
};

/**
 * @param {String} mode
 * @param {Object} callbacks
 */
export const modeGate = (mode, callbacks = {}) => {
  switch (mode) {
    case SCHEDULE_MODE.DAILY:
    default: {
      if (_.isFunction(callbacks[SCHEDULE_MODE.DAILY])) {
        callbacks[SCHEDULE_MODE.DAILY]();
      }

      break;
    }
    case SCHEDULE_MODE.WEEKLY: {
      if (_.isFunction(callbacks[SCHEDULE_MODE.WEEKLY])) {
        callbacks[SCHEDULE_MODE.WEEKLY]();
      }

      break;
    }
    case SCHEDULE_MODE.MOBILE: {
      if (_.isFunction(callbacks[SCHEDULE_MODE.MOBILE])) {
        callbacks[SCHEDULE_MODE.MOBILE]();
      }

      break;
    }
  }
};

/**
 * @param {Object} task
 * @param {Array} people
 * @return {Object}
 */
export const buildCardTaskItem = (task, people) => {
  const assignedPeople = peopleHelpers.getCurrentlyAssignedPeople(task, people);
  const departmentCode = taskHelper.getDepartmentCode(task);
  const isDraft = taskHelper.isDraft(task);
  const isPaused = taskHelper.isTaskPaused(task);
  const isStarted = taskHelper.isTaskStarted(task);
  const lifeStatus = taskHelper.getLifeStatus(task);
  const property = _.get(task, 'home', {});
  const startTime = _.get(task, 'start_time');
  const taskCompany = _.get(task, 'company', {});
  const taskDraftId = taskHelper.getTaskSeriesId(task);
  const taskId = _.get(task, 'id');
  const taskName = taskHelper.getTaskName(task);
  const isRecurs = !!taskDraftId;

  return {
    assignedPeople,
    departmentCode,
    isDraft,
    isPaused,
    isRecurs,
    isStarted,
    lifeStatus,
    property,
    startTime,
    taskCompany,
    taskDraftId,
    taskId,
    taskName,
  };
};

/**
 * @param {Object} tasksSummary
 * @return {Array.<String>}
 */
export const getDatesWithTasks = tasksSummary => {
  const datesWithTasks = [];

  _.forIn(tasksSummary, (tasksCount, dateKey) => {
    if (tasksCount > 0) {
      datesWithTasks.push(dateKey);
    }
  });

  return datesWithTasks;
};

/**
 * @param {Number} numberOfDays
 * @param {Number} numberOfRows
 * @param {Number} rowIndex
 * @param {Number} columnIndex
 * @param {Object} cellSizes
 * @param {Boolean} withFlyout
 * @return {Object}
 */
export const countPosition = (
  numberOfDays,
  numberOfRows,
  rowIndex,
  columnIndex,
  cellSizes,
  withFlyout,
) => {
  const position = {};
  const leftMargin = 11;
  const height = 320;
  const width = 192;
  const extraRightSpaceForFlyout = withFlyout ? 500 : 0;

  const resetNumberOfDay = numberOfDays - 1;
  const resetNumberOfRows = numberOfRows - 1;
  const minWidth = cellSizes.width < width ? width : cellSizes.width;
  const cellWidthWithMinWidth =
    columnIndex === resetNumberOfDay ? minWidth + leftMargin : minWidth + leftMargin * 2;

  if (rowIndex === resetNumberOfRows) {
    position.bottom = 0;
  } else {
    const halfSizeOfCell = (height - cellSizes.height) / 2;
    position.bottom = cellSizes.bottom - halfSizeOfCell;
  }

  if (columnIndex === resetNumberOfDay) {
    position.right = extraRightSpaceForFlyout;
  } else {
    const flowsOutSpace = (cellWidthWithMinWidth - cellSizes.width) / 2;
    position.right = extraRightSpaceForFlyout + cellSizes.right - flowsOutSpace;
  }
  position.width = cellWidthWithMinWidth;

  return position;
};

export const getCellPositionConfiguration = (
  numberOfRows,
  numberOfColumns,
  columnIndex,
  rowIndex,
) => {
  if (numberOfColumns === columnIndex && numberOfRows > rowIndex) {
    return {
      nextDayColumn: 0,
      nextDayRow: rowIndex + 1,
    };
  } else if (numberOfColumns > columnIndex) {
    return {
      nextDayRow: rowIndex,
      nextDayColumn: columnIndex + 1,
    };
  }

  return {
    nextDayRow: null,
    nextDayColumn: null,
  };
};

const getConflictsDates = (reservations, reservationId, newReservations, key) => {
  const reservationObject = newReservations[reservationId];
  const { start } = reservationObject;
  const moreReservationsThanOne = _.size(reservations) > 1;
  const reservationIdExistsInReservations = _.includes(_.map(reservations, 'id'), reservationId);
  const reservationStartIsCheckinCheckout = start === 'checkin' || start === 'checkout';
  const reservationConflictsDates = _.get(newReservations, [reservationId, 'conflicts_dates'], []);

  return moreReservationsThanOne &&
    !(reservationIdExistsInReservations && reservationStartIsCheckinCheckout)
    ? [...reservationConflictsDates, key]
    : [];
};

export const getLimitedReservationListedByDate = data =>
  _.reduce(
    data,
    (result, value, key) => {
      const newReservations = {};
      _.each(value.reservations, reservation => {
        const { checkin_date: checkinDate, checkout_date: checkoutDate } = reservation;
        const reservationId = reservation.id;

        newReservations[reservationId] = { ..._.get(newReservations, [reservationId]) };

        const reservationObject = newReservations[reservationId];

        if (_.isEmpty(_.get(newReservations, [reservationId])) && key === checkinDate) {
          reservationObject.start = 'checkin';
        } else if (
          _.isEmpty(_.get(newReservations, [reservationId])) &&
          key !== checkinDate &&
          key !== checkoutDate
        ) {
          reservationObject.start = 'occupied';
        } else if (_.isEmpty(_.get(newReservations, [reservationId])) && key === checkoutDate) {
          reservationObject.start = 'checkout';
        }

        reservationObject.checkin = checkinDate;
        reservationObject.checkout = checkoutDate;

        reservationObject.conflicts_dates = getConflictsDates(
          value.reservations,
          reservationId,
          newReservations,
          key,
        );
      });
      result[key] = newReservations;
      return result;
    },
    {},
  );

export const listReservationsGroupedById = data => {
  const reservationList = {};
  const limitedData = getLimitedReservationListedByDate(data);

  _.each(limitedData, (reservations, date) => {
    _.each(reservations, (reservation, key) => {
      if (_.isEmpty(reservationList[key])) {
        reservationList[key] = { ...reservation };
      } else {
        reservationList[key].conflicts_dates = [
          ..._.get(reservationList, [key, 'conflicts_dates'], []),
          ...reservation.conflicts_dates,
        ];
      }
      reservationList[key].dates = [..._.get(reservationList, [key, 'dates'], []), date];
    });
  });

  return reservationList;
};

const sumDurationOfReservations = (data, reservation) =>
  _.sumBy(data, date => {
    const { end_date: endDate, start_date: startDate, end, start } = reservation;
    const checkinOrCheckout = type => type === 'checkin' || type === 'checkout';
    const startAndEndTheSameDay = date === endDate && date === startDate;
    const endDateIsCheckinCheckout = date === endDate && checkinOrCheckout(end);
    const startDateIsCheckinCheckout = date === startDate && checkinOrCheckout(start);

    if (startAndEndTheSameDay || endDateIsCheckinCheckout || startDateIsCheckinCheckout) {
      return 0.5;
    }
    return 1;
  });

const reservationDuration = reservation => {
  const reservationEndDate = reservation.end_date;
  const reservationStartDate = reservation.start_date;
  const reservationDates = reservation.dates;

  const endOfWeek = moment(reservationStartDate)
    .endOf('week')
    .format('YYYY-MM-DD');

  const endDate =
    reservationEndDate >= endOfWeek
      ? _.indexOf(reservationDates, endOfWeek)
      : _.indexOf(reservationDates, reservationEndDate);

  const existingDates = _.slice(
    reservationDates,
    _.indexOf(reservationDates, reservationStartDate),
    endDate + 1,
  );

  return sumDurationOfReservations(existingDates, reservation);
};

export const countDurationFromListOfReservations = data => {
  const limitedData = listReservationsGroupedById(data);

  return _.reduce(
    limitedData,
    (result, value, key) => {
      const firstDate = _.first(_.get(value, 'dates'));
      const lastDate = _.last(_.get(value, 'dates'));
      const checkin = _.get(value, 'checkin');
      const checkout = _.get(value, 'checkout');
      const conflictsDates = _.get(value, 'conflicts_dates');

      result[key] = value;
      result[key].start_date = firstDate > checkin ? firstDate : checkin;

      if (!_.isEmpty(conflictsDates)) {
        result[key].end_date = _.first(conflictsDates);
      } else {
        result[key].end_date = checkout > lastDate ? lastDate : checkout;
      }

      const reservationEndDate = result[key].end_date;

      result[key].end =
        _.includes(conflictsDates, reservationEndDate) || reservationEndDate === checkout
          ? 'checkout'
          : 'occupied';

      const checkinOrCheckout = type => type === 'checkin' || type === 'checkout';

      if (
        result[key].end === result[key].start &&
        checkinOrCheckout(result[key].end) &&
        checkinOrCheckout(result[key].start)
      ) {
        result[key].duration = 0.5;
      } else {
        result[key].duration = reservationDuration(result[key]);
      }

      return result;
    },
    {},
  );
};
